// React Query hooks for the member-performance pages. Backed by
// /api/analytics/members and /api/analytics/members/:userId, which are
// gated to manager+ on the server.

import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../../lib/api-client";

export interface MemberOverviewRow {
  userId: string;
  name: string;
  email: string;
  role: string;
  isActive: boolean;
  activeLeads: number;
  newLeads: number;
  activitiesLogged: number;
  converted: number;
  conversionRate: number;
  unattended24h: number;
  avgFirstResponseSec: number | null;
  lastActivityAt: string | null;
}

export interface MembersOverviewResponse {
  days: number;
  members: MemberOverviewRow[];
}

export interface MemberDetailResponse {
  days: number;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
    isActive: boolean;
  };
  metrics: MemberOverviewRow;
  funnel: { status: string; count: number }[];
  sources: { source: string; campaign: string | null; count: number }[];
  activityTypes: { type: string; count: number }[];
  recentActivities: {
    id: string;
    leadId: string;
    leadName: string | null;
    type: string;
    outcome: string | null;
    body: string | null;
    occurredAt: string;
  }[];
  assignedLeads: {
    id: string;
    name: string | null;
    company: string | null;
    status: string;
    source: string;
    lastActivityAt: string | null;
    lastLoggedActivityType: string | null;
    lastLoggedActivityAt: string | null;
    createdAt: string;
  }[];
}

export function useMembersOverview(
  workspaceSlug: string | null | undefined,
  days: number
) {
  return useQuery({
    enabled: !!workspaceSlug,
    queryKey: ["analytics", "members", workspaceSlug, days],
    queryFn: () =>
      apiClient.get<MembersOverviewResponse>("/api/analytics/members", {
        params: { workspace: workspaceSlug ?? undefined, days },
      }),
    staleTime: 60_000,
  });
}

export function useMemberDetail(
  workspaceSlug: string | null | undefined,
  userId: string | undefined,
  days: number
) {
  return useQuery({
    enabled: !!workspaceSlug && !!userId,
    queryKey: ["analytics", "members", workspaceSlug, "detail", userId, days],
    queryFn: () =>
      apiClient.get<MemberDetailResponse>(
        `/api/analytics/members/${userId}`,
        { params: { workspace: workspaceSlug ?? undefined, days } }
      ),
    staleTime: 60_000,
  });
}

// Self-serve variant — every authenticated user can fetch their own metrics
// without the manager+ gate that protects /members/:userId.
export function useMyMemberDetail(
  workspaceSlug: string | null | undefined,
  days: number
) {
  return useQuery({
    enabled: !!workspaceSlug,
    queryKey: ["analytics", "members", workspaceSlug, "me", days],
    queryFn: () =>
      apiClient.get<MemberDetailResponse>(`/api/analytics/members/me`, {
        params: { workspace: workspaceSlug ?? undefined, days },
      }),
    staleTime: 60_000,
  });
}
